import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-06cd279a"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = ["id", "onClick"]
const _hoisted_3 = ["id"]

import * as PDFJS from 'pdfjs-dist'
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api'
import { onMounted, PropType, ref, toRaw, watch } from 'vue'
/**
 * define props
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'RenderPage',
  props: {
  currentPage: {
    type: Number,
    default: 1,
  },
  docPath: {
    type: String,
    required: true,
  },
  docPageKeyStore: {
    type: String,
    default: '',
  },
  scaleText: {
    type: String,
    default: '1',
  },
  highlightActive: {
    type: Boolean,
    default: false,
  },
  canClickPage: {
    type: Boolean,
    default: false,
  },
  scrollStyle: {
    type: String as PropType<'smooth' | 'instant'>,
    default: 'instant',
  },
  detectScroll: {
    type: Boolean,
    default: false,
  },
},
  emits: ['change-page', 'load-success', 'click-page'],
  setup(__props, { emit: __emit }) {

PDFJS.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${PDFJS.version}/build/pdf.worker.min.mjs`
const props = __props

const emit = __emit

const activePage = ref<number>(1)
const containerRef = ref<HTMLElement>()
const parentId = ref<string>(Math.random().toString(36).substr(2, 6))
const totalPage = ref<number>(0)
const scale = ref<number>(1)

const pdf = ref<PDFDocumentProxy>()
const pdfLoadingTask = ref<PDFJS.PDFDocumentLoadingTask>()

/**
 * Returns an array of the actual page width and height based on props and
 * aspect ratio.
 * @param ratio - Page aspect ratio.
 */
const getPageDimensions = (ratio: number): [number, number] => {
  let width: number
  let height: number

  width = document.getElementById(parentId.value)?.clientWidth || 300
  height = width * ratio

  return [width, height]
}

const adjustPageSize = async () => {
  const pageNums = [...Array(totalPage.value + 1).keys()].slice(1)
  const pdfDocProxy = toRaw(pdf.value)
  if (!pdfDocProxy) return
  for (const num of pageNums) {
    const page = await pdfDocProxy.getPage(num)
    const viewWidth = page.view[2] - page.view[0]
    const viewHeight = page.view[3] - page.view[1]
    const [actualWidth] = getPageDimensions(viewHeight / viewWidth)

    const pageWidth = viewWidth
    if (props.scaleText === 'fit-width') {
      scale.value = actualWidth / pageWidth
    } else if (props.scaleText === 'fit-height') {
      const pageActualHeight = document.getElementById(`${parentId.value}`)?.clientHeight
      scale.value = pageActualHeight ? pageActualHeight / viewHeight : 1
    } else {
      scale.value = Number(props.scaleText)
    }
    const viewport = page.getViewport({
      scale: scale.value,
    })

    const canvas = document.getElementById(`c-${parentId.value}-${num}`) as HTMLCanvasElement
    if (canvas) {
      canvas.width = 1
      canvas.height = 1
      canvas.getContext('2d')?.clearRect(0, 0, 1, 1)
      canvas.height = Math.floor(viewport.height)
      canvas.width = Math.floor(viewport.width)
      canvas.style.width = Math.floor(viewport.width) + 'px'
      canvas.style.height = Math.floor(viewport.height) + 'px'
      // Render PDF page into canvas context
      page.render({
        canvas: canvas,
        canvasContext: canvas.getContext('2d') || undefined,
        viewport: viewport,
      }).promise
    }
  }
}

const scrollToPageAfterLoad = () => {
  scrollToPage(props.currentPage, 'instant')
  activePage.value = props.currentPage
}

const loadPDF = async () => {
  pdfLoadingTask.value = PDFJS.getDocument(props.docPath)
  pdfLoadingTask.value.promise
    .then(async function (pdfDoc) {
      pdf.value = pdfDoc
      totalPage.value = pdfDoc.numPages

      await adjustPageSize()
      emit('load-success', { totalPage: totalPage.value, scale: scale.value })
      scrollToPageAfterLoad()
      if (props.detectScroll) {
        containerRef?.value?.addEventListener('scroll', () => {
          const pageHeight =
            (document.getElementById(`${parentId.value}-1`)?.offsetHeight || 0) + 16

          if (containerRef?.value) {
            const scrollTop = containerRef?.value?.scrollTop
            const latestPage = Math.round(scrollTop / pageHeight) + 1
            const page = latestPage >= totalPage.value ? totalPage.value : latestPage
            activePage.value = page
            emit('change-page', page)
          }
        })
      }
    })
    .catch(function (error) {
      // Handle any errors during loading
      console.error('Error loading PDF:', error.message)
    })
}

const scrollToPage = (page: number, style?: 'smooth' | 'instant') => {
  const elementPage = document.getElementById(`${parentId.value}-${page}`)
  if (elementPage) {
    elementPage.scrollIntoView({ behavior: style || props.scrollStyle })
  }
}

const clickChangePage = (page: number) => {
  emit('click-page', page)
}

watch(
  () => props.currentPage,
  () => {
    if (activePage.value !== props.currentPage) {
      activePage.value = props.currentPage
      scrollToPage(activePage.value)
    }
  },
)
watch(
  () => props.scaleText,
  () => {
    adjustPageSize()
  },
)

onMounted(() => {
  loadPDF()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "containerRef",
    ref: containerRef,
    class: "pages-container"
  }, [
    _createElementVNode("div", {
      id: parentId.value,
      class: "w-full h-full"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(totalPage.value, (pageNum) => {
        return (_openBlock(), _createElementBlock("div", {
          key: pageNum,
          class: "mb-4"
        }, [
          _renderSlot(_ctx.$slots, "before-page", { page: pageNum }),
          _createElementVNode("div", {
            id: parentId.value && `${parentId.value}-${pageNum}`,
            style: _normalizeStyle({
            '--scale-factor': scale.value,
            position: 'relative',
            opacity: __props.highlightActive ? (pageNum === activePage.value ? 1 : 0.7) : 1,
            cursor: __props.canClickPage ? 'pointer' : 'default',
          }),
            onClick: ($event: any) => (clickChangePage(pageNum))
          }, [
            _createElementVNode("canvas", {
              id: `c-${parentId.value}-${pageNum}`
            }, null, 8, _hoisted_3)
          ], 12, _hoisted_2),
          _renderSlot(_ctx.$slots, "after-page", { page: pageNum })
        ]))
      }), 128))
    ], 8, _hoisted_1)
  ], 512))
}
}

})